import {
  createUserWithEmailAndPassword,
  signInWithEmailAndPassword,
  signOut,
  sendPasswordResetEmail,
} from "firebase/auth";
import { doc, setDoc, getDoc } from "firebase/firestore";
import { auth, db } from "../firebase";
import { t } from "../i18n/translations";
import { generateId } from "../utils/idUtils";
import { detectCountry } from "../utils/currencyUtils";

export function createAuthHandlers(ctx) {
  const {
    registerForm, loginForm, landingLang, joinMode, joinFamilyId,
    lang,
    setUser, setUserProfile, setScreen, setActiveTab,
    setCurrentFamily, setUserFamilies, setViewingMember, setViewingWishlist,
    setMembers,
    setJoinMode,
    setAuthError,
    setExistingWishlists, setWishlistChoice, setSelectedExistingId,
    setPendingJoinFamilyId, setShowWishlistModal,
    showToast, tr, 
    setShowTutorial, setSharedMode,     
  } = ctx;

  // ── REGISTER ──
  const handleRegister = async function () {
    setAuthError("");
    if (!registerForm.name || !registerForm.email || !registerForm.password) {
      setAuthError(t(registerForm.language, "fillFields"));
      return;
    }
    try {
      const cred = await createUserWithEmailAndPassword(
        auth,
        registerForm.email,
        registerForm.password
      );
      const uid = cred.user.uid;

      if (joinMode && joinFamilyId) {
        // Guardar groupNickname al unirse a un grupo
        const nickname = (registerForm.groupNickname || "").trim();
        const groupNicknames = nickname ? { [joinFamilyId]: nickname } : {};

        await setDoc(doc(db, "users", uid), {
          name: registerForm.name,
          email: registerForm.email,
          birthday: registerForm.birthday || "",
          color: registerForm.color || "#9b59b6",
          language: registerForm.language,
          families: [],
          familyWishlists: {},
          wishlists: [],
          country: detectCountry(),
          groupNicknames,
        });
        const newProfile = {
          name: registerForm.name,
          email: registerForm.email,
          birthday: registerForm.birthday || "",
          color: registerForm.color || "#9b59b6",
          language: registerForm.language,
          families: [],
          familyWishlists: {},
          wishlists: [],
          country: detectCountry(),
          groupNicknames,
        };
        setUserProfile(newProfile);
        setUser(cred.user);
        setPendingJoinFamilyId(joinFamilyId);
        setExistingWishlists([]);
        setWishlistChoice("new");
        setSelectedExistingId(null);
        setScreen("family");
        setShowWishlistModal(true);
      } else {
        const familyId = generateId();
        const wishlistId = generateId();
        await setDoc(doc(db, "wishlists", wishlistId), {
          ownerId: uid,
          name: registerForm.name + "'s list",
          gifts: [],
        });
        await setDoc(doc(db, "families", familyId), {
          name: ctx.familySetup?.name || "Our Family",
          leaderId: uid,
          memberIds: [uid],
          createdAt: Date.now(),
        });
        await setDoc(doc(db, "users", uid), {
          name: registerForm.name,
          email: registerForm.email,
          birthday: registerForm.birthday || "",
          color: registerForm.color || "#9b59b6",
          language: registerForm.language,
          families: [familyId],
          familyWishlists: { [familyId]: wishlistId },
          wishlists: [wishlistId],
          country: detectCountry(),
          groupNicknames: {},
        });
        const newFamily = { id: familyId, name: ctx.familySetup?.name || "Our Family", leaderId: uid, memberIds: [uid] };
  const newProfile = {
    name: registerForm.name,
    email: registerForm.email,
    birthday: registerForm.birthday || "",
    color: registerForm.color || "#9b59b6",
    language: registerForm.language,
    families: [familyId],
    familyWishlists: { [familyId]: wishlistId },
    wishlists: [wishlistId],
    country: detectCountry(),
    groupNicknames: {},
  };
  ctx.setCurrentFamily(newFamily);
        ctx.setUserFamilies([newFamily]);
        ctx.setUserProfile(newProfile);
        ctx.setUser(cred.user);
        showToast(t(registerForm.language, "welcomeLeader"));
        setScreen("family");
        setActiveTab("wishlu");
        const tutorialDone = (() => {
          try { return localStorage.getItem("whislu_tutorial_done"); }
          catch(e) { return null; }
        })();
        if (!tutorialDone) setShowTutorial(true);
      }
    } catch (e) {
      if (e.code === "auth/email-already-in-use")
        setAuthError(t(registerForm.language, "emailInUse"));
      else if (e.code === "auth/weak-password")
        setAuthError(t(registerForm.language, "weakPassword"));
      else setAuthError(t(registerForm.language, "signUpFailed"));
    }
  };

  // ── FORGOT PASSWORD ──
  const handleForgotPassword = async function () {
    const email = loginForm.email.trim();
    if (!email) {
      setAuthError(
        lang === "es" ? "Ingresa tu correo primero"
        : lang === "fi" ? "Anna ensin sähköpostisi"
        : "Enter your email first"
      );
      return;
    }
    try {
      await sendPasswordResetEmail(auth, email);
      showToast(
        lang === "es" ? "📧 Correo de recuperación enviado"
        : lang === "fi" ? "📧 Palautussähköposti lähetetty"
        : "📧 Recovery email sent"
      );
      setAuthError("");
    } catch (e) {
      setAuthError(
        lang === "es" ? "No se encontró esa cuenta"
        : lang === "fi" ? "Tiliä ei löydy"
        : "No account found for that email"
      );
    }
  };

  // ── LOGIN ──
  const handleLogin = async function () {
    setAuthError("");
    try {
      const savedHash = window.location.hash;
      const cred = await signInWithEmailAndPassword(
        auth,
        loginForm.email,
        loginForm.password
      );
      const snap = await getDoc(doc(db, "users", cred.user.uid));
      if (!snap.exists()) return;

      const profile = snap.data();
      setUserProfile(profile);

      if (joinMode && joinFamilyId) {
        const alreadyIn = (profile.families || []).includes(joinFamilyId);
        if (!alreadyIn) {
          setPendingJoinFamilyId(joinFamilyId);
          const ids = profile.wishlists || [];
          const snaps = await Promise.all(
            ids.map((id) => getDoc(doc(db, "wishlists", id)))
          );
          const wls = snaps.filter((s) => s.exists()).map((s) => ({ id: s.id, ...s.data() }));
          setExistingWishlists(wls);
          setWishlistChoice(wls.length > 0 ? "existing" : "new");
          setSelectedExistingId(wls.length > 0 ? wls[0].id : null);
          setScreen("family");
          setShowWishlistModal(true);
        } else {
          showToast(t(profile.language || "en", "welcome") + ", " + profile.name + "!");
          window.location.hash = "";
          setJoinMode(false);
          if (profile.families?.length > 0) {
            const fSnaps = await Promise.all(
              profile.families.map((fid) => getDoc(doc(db, "families", fid)))
            );
            const fams = fSnaps.filter((s) => s.exists()).map((s) => ({ id: s.id, ...s.data() }));
            setUserFamilies(fams);
            setCurrentFamily(fams.find((f) => f.id === joinFamilyId) || fams[0]);
          }
          setScreen("family");
        }
        return;
      }

      showToast(t(profile.language || landingLang, "welcomeBack") + "!");
      if (savedHash && savedHash.startsWith("#/") && !savedHash.includes("join")) {
  window.location.hash = savedHash;
  setSharedMode(true);
  setScreen("family");
  return;
}

      let firstFamily = null;
      if (profile.families?.length > 0) {
        const fSnaps = await Promise.all(
          profile.families.map((fid) => getDoc(doc(db, "families", fid)))
        );
        const fams = fSnaps.filter((s) => s.exists()).map((s) => ({ id: s.id, ...s.data() }));
        setUserFamilies(fams);
        firstFamily = fams[0] || null;
        setCurrentFamily(firstFamily);
      }
      const myWishlistId = firstFamily
        ? (profile.familyWishlists || {})[firstFamily.id]
        : null;
      if (myWishlistId) {
        const wSnap = await getDoc(doc(db, "wishlists", myWishlistId));
        if (wSnap.exists()) {
          setViewingMember({ id: cred.user.uid, ...profile });
          setViewingWishlist({ id: wSnap.id, ...wSnap.data() });
          setActiveTab("wishlu");
          setScreen("profile");
          return;
        }
      }
      setActiveTab("wishlu");
      setScreen("family");
    } catch (e) {
      setAuthError(tr("incorrectCredentials"));
    }
  };

  // ── LOGOUT ──
  const handleLogout = async function () {
  try {
    localStorage.removeItem("whislu_profile");
    localStorage.removeItem("whislu_family");
    localStorage.removeItem("whislu_members");
    localStorage.removeItem("whislu_families");
  } catch (e) {}
  setUserProfile(null);
  setCurrentFamily(null);
  setUserFamilies([]);
  setViewingMember(null);
  setViewingWishlist(null);
  setMembers([]);
  setScreen("family");
  setActiveTab("wishlu");
  await signOut(auth);
  showToast(tr("seeYouSoon"));
};

  return { handleRegister, handleForgotPassword, handleLogin, handleLogout };
}
